import Game from './Game';
import { PlayerActions } from './State/Player/PlayerActions';
import { FTUXType } from './Player/PlayerProperty/Progress/FTUXType';
import { GameActions } from './State/Game/GameActions';
import { RewardType } from './Utility/RewardHelper';
import { LevelType, IPlayerLevelData } from './State/Player/Progress/PlayerLevels';
import { WorldDataHelper } from './Utility/WorldDataHelper';
import { ILimit, MathHelper } from './Utility/MathHelper';
import { PlayerStateHelper } from './State/Player/PlayerStateHelper';
import { BoosterType, PreBoosterType } from './State/Card/BoosterType';
import { TimerType } from './System/Timer/TimerTypes';
import DateHelper from './Utility/DateHelper';
import WorldSystem from './System/World/WorldSystem';
import AnimationTask from './Utility/Task/AnimationTask';
import { PlayerTask } from './State/Player/PlayerTasks';
import { GBCHandler } from './Utility/SDK/GBCHandler';
import { playerDataKeysToLoad } from './Player/IPlayerData';
import { IGameState } from './State/Game/IGameState';
import { IPlayerState } from './State/Player/IPlayerState';
import { UIActions } from './State/UI/UIActions';
import { StarChestHelper } from './Utility/StarChestHelper';
import { StartAnimationClip } from './System/Animation/AnimationHelper';
import { StarChestUpgrade } from './System/Animation/Clip';
import { questSlotsCount, questTimerDuration } from './Constants/Quests';
import { QuestType } from './State/Player/QuestType';
import QuestHelper from './Utility/QuestHelper';
import DailyLoginHelper from './Utility/DailyLoginHelper';
import { PetsStateHelper } from './State/Player/Pets/PetsStateHelper';
import Point from './Utility/Point';
import { MovePet } from './System/Animation/Clip/MovePet';
import { PetAnimations } from './Utility/Helpers/Pets/PetAnimations';
import { PetsHelper } from './Utility/Helpers/Pets/PetsHelper';
import { PetId } from './Constants/Pets';
import { petDayIndex } from './Constants/DailyLogin';
import { EntryPoint } from './Utility/EntryPoint/EntryPointHelper';
import { decryptPayload, encryptPayload } from './Utility/EntryPoint/EntryPointEncryption';
import { initialPetsState } from './State/Player/Pets/initialPetsState';
import { MyPetMiniGameSceneParams } from './Scene/MyPetMiniGameScene';
import { QuestStateHelper } from './State/Player/Quests/QuestStateHelper';
import { ArrayHelper } from './Utility/ArrayHelper';
import { StarChestStateHelper } from './State/Player/StarChests/StarChestStateHelper';
import { ProgressStateHelper } from './State/Player/Progress/ProgressStateHelper';
import TimerStateHelper from './State/Timer/TimerStateHelper';
import { FTUXStateHelper } from './State/Game/FTUX/FTUXStateHelper';
import { PopupID } from './State/UI/PopupID';
import './Utility/WebSocketCommunication';
import { setForceHelperFriend } from './State/Level/HelperFriend/HelperFriendStateHelper';
import { initialPlayerState } from './State/Player/initialPlayerStates';
import { scheduleInactivityNotification } from 'State/Game/Action/ScheduleNotification';
import { IMessagePayload } from 'State/Game/IEntryPoint';
import PayloadHelper from 'Utility/SDK/PayloadHelper';
import { TimerActions } from 'State/Timer/TimerActions';
import { FarmActions } from 'State/Farm/FarmActions';
import { getFieldData, getFieldSectorFromId, getUnlockedFields } from 'System/Field/FieldHelper';
import { farmTransitionSceneId, GameScene } from 'State/Game/GameScene';
import { initialFarmPlayerState } from 'State/Player/Farm/initialFarmPlayerState';

declare const FBInstant: any;

export let cheatsUsed: boolean = false;

export const cheats = {
    clearPlayerData: () => {
        // tslint:disable-next-line:no-console
        console.log('Clearing player data - please wait...');
        Game.sdk.save = async () => void 0;

        Game.sdk.clearPlayerData().then(() => {
            // tslint:disable-next-line:no-console
            console.log('Player data cleared. Please restart the game.');
            location.reload();
        });
    },
    fetchFBData: (keys: Array<string> = playerDataKeysToLoad) => {
        return FBInstant.player.getDataAsync(keys);
    },
    fetchGBCData: () => {
        return GBCHandler.debugLoadFromGBC();
    },
    pauseHarvestTimer: (paused: boolean) => {
        Game.settings.harvestPaused = paused;
        return `Harvest timer has ${paused ? 'paused' : 'resumed'}`;
    },
    resetFriendData: () => {
        const playerState = Game.store.getState().playerState;

        PlayerActions.setPlayerState({
            ...playerState,
            social: {
                ...playerState.social,
                friendData: [],
            },
        });
    },
    resetFTUX: (unlock?: FTUXType) => {
        PlayerActions.clearFTUX(unlock);
    },
    resetFBEvent: () => {
        Game.sdk.debugResetFBEvents();
    },
    resetFields: (field?: string, reload: boolean = true) => {
        PlayerActions.clearField(field);
        if (reload) {
            GameActions.loadWorld();
        }
    },
    resetTimers: (reload: boolean = true) => {
        const playerState = Game.store.getState().playerState;
        const emptyTimerMap: Map<string, Date> = new Map<string, Date>();

        playerState.timers.forEach((date: Date, key: string) => emptyTimerMap.set(key, null));

        PlayerActions.setPlayerState({
            ...playerState,
            timers: emptyTimerMap,
            social: {
                ...playerState.social,
                friendData: Game.store.getState().playerState.social.friendData.map(friend => ({
                    ...friend,
                    currencySendTimer: null,
                    currencyRequestTimer: null,
                    giftCooldown: null,
                    helperTimer: null,
                })),
            },
        });

        if (reload) {
            GameActions.loadWorld();
        }
    },
    resetFarmMiniGame: (reload: boolean = true) => {
        TimerActions.updateTimer({
            timer: TimerType.FARM_MINI_GAME,
            time: null,
        });

        if (reload) {
            GameActions.loadFarmScene({loadingSceneId: farmTransitionSceneId});
        }
    },
    setCoins: (value: number) => {
        const playerState = Game.store.getState().playerState;

        PlayerActions.setPlayerState({
            ...playerState,
            inventory: {
                ...playerState.inventory,
                coins: value,
            },
        });
    },
    setLevel: (level: number, stars: number = 3, levelType: LevelType = LevelType.Core) => {
        stars = Math.max(Math.min(stars, 3), 0);

        const levelData: IPlayerLevelData[] = [];
        for (let i = 1; i <= level; i++) {
            levelData.push({ levelNumber: i, stars: stars });
        }
        const state: IGameState = Game.store.getState();
        let playerState: IPlayerState = state.playerState;

        // Reset star chest if cheating past the current star chest
        if (level > playerState.starChestLevel) {
            playerState = StarChestStateHelper.setStarChestLevel(state.playerState, null);
        }

        let worldLimits: ILimit = WorldDataHelper.getWorldLevelLimits(playerState.maxWorld);
        while (!MathHelper.isWithinRange(level, worldLimits.min, worldLimits.max)) {
            if (level > worldLimits.max) {
                playerState = ProgressStateHelper.unlockNextWorld(playerState);
            } else if (level < worldLimits.min) {
                playerState = {
                    ...playerState,
                    maxWorld: playerState.maxWorld - 1,
                };
            }
            worldLimits = WorldDataHelper.getWorldLevelLimits(playerState.maxWorld);
        }
        if (!FTUXStateHelper.isFtuxCompleted(playerState, FTUXType.HARVEST) && level > 2) {
            playerState = FTUXStateHelper.updateFTUXProgress(playerState, [FTUXType.HARVEST]);
        }

        // Subtract one from `playerState.maxWorld` to still show unlocks for the current world.
        const allUnlockedFields = getUnlockedFields(level, playerState.maxWorld - 1);

        // Get existing field Ids that may still be unlocked in the current world.
        const existingFields = playerState.fields.filter(field => {
            const index = getFieldSectorFromId(field); // sector === index
            const requiredLevel = getFieldData(field).requiredLevel[index];
            return level >= requiredLevel;
        });

        // Merge current fields with new unlocked fields.
        const fields = [...new Set([...existingFields, ...allUnlockedFields])];

        PlayerActions.setPlayerState({
            ...playerState,
            levels: {
                ...playerState.levels,
                [levelType]: levelData,
            },
            fields,
        });

        GameActions.loadWorld({ firstComplete: true });
        return 'Level Set. Reloading World..';
    },
    setWorld: (maxWorld: number) => {
        let state: IGameState = Game.store.getState();
        let levelLimit: keyof ILimit = 'min';
        if (maxWorld > state.playerState.maxWorld) {
            while (state.playerState.maxWorld < maxWorld) {
                state.playerState = ProgressStateHelper.unlockNextWorld(state.playerState);
            }
            levelLimit = 'min';
        } else {
            state.worldState.worldNumber = maxWorld;
            state.playerState.maxWorld = maxWorld;
            levelLimit = 'max';
        }
        GameActions.setState(state);

        const playerLevel: number = Game.player.getHighestLevelCompleted();
        const worldLimits: ILimit = WorldDataHelper.getWorldLevelLimits(state.playerState.maxWorld);
        if (!MathHelper.isWithinRange(playerLevel, worldLimits.min, worldLimits.max)) {
            GameCheats.setLevel(worldLimits[levelLimit] - 1);
        }
        GameActions.loadWorld({
            worldNumber: maxWorld,
            firstComplete: levelLimit === 'max',
        });
    },
    setBoosterTNT: (amount: number) => {
        const playerState = Game.store.getState().playerState;
        PlayerActions.receiveBoosterTNT(amount - playerState.inventory[BoosterType.TNT]);
    },
    setBoosterBullsEye: (amount: number) => {
        const playerState = Game.store.getState().playerState;
        PlayerActions.receiveBoosterBullsEye(amount - playerState.inventory[BoosterType.BULLS_EYE]);
    },
    setBoosterShuffle: (amount: number) => {
        const playerState = Game.store.getState().playerState;
        PlayerActions.receiveBoosterShuffle(amount - playerState.inventory[BoosterType.SHUFFLE]);
    },
    setBoosterUndo: (amount: number) => {
        const playerState = Game.store.getState().playerState;
        PlayerActions.receiveBoosterUndo(amount - playerState.inventory[BoosterType.UNDO]);
    },
    setBoosterJoker: (amount: number) => {
        const playerState = Game.store.getState().playerState;
        PlayerActions.receiveBoosterJoker(amount - playerState.inventory[BoosterType.JOKER]);
    },
    setPreBoosterCanister: (amount: number) => {
        const playerState = Game.store.getState().playerState;

        PlayerActions.setPlayerState({
            ...playerState,
            inventory: {
                ...playerState.inventory,
                [PreBoosterType.CANISTER]: amount,
            },
        });
    },
    setPreBoosterJoker: (amount: number) => {
        const playerState = Game.store.getState().playerState;

        PlayerActions.setPlayerState({
            ...playerState,
            inventory: {
                ...playerState.inventory,
                [PreBoosterType.JOKER]: amount,
            },
        });
    },
    setPreBoosterRemoveThree: (amount: number) => {
        const playerState = Game.store.getState().playerState;

        PlayerActions.setPlayerState({
            ...playerState,
            inventory: {
                ...playerState.inventory,
                [PreBoosterType.REMOVE_THREE]: amount,
            },
        });
    },
    setFreePlay: (amount: number) => {
        const playerState = Game.store.getState().playerState;

        PlayerActions.setPlayerState({
            ...playerState,
            inventory: {
                ...playerState.inventory,
                [RewardType.FREE_PLAY]: amount,
            },
        });
    },
    setShopGift: (amount: number) => {
        const playerState = Game.store.getState().playerState;

        PlayerActions.setPlayerState({
            ...playerState,
            inventory: {
                ...playerState.inventory,
                shopGift: amount,
            },
        });
    },
    setAdsWatched: (amount: number) => {
        const playerState = Game.store.getState().playerState;

        PlayerActions.setPlayerState({
            ...playerState,
            progress: {
                ...playerState.progress,
                adsWatched: amount,
            },
        });
    },
    setBoosterPlusFive: (amount: number) => {
        const playerState = Game.store.getState().playerState;
        PlayerActions.debugReceiveBoosterPlusFive(amount - playerState.inventory[BoosterType.PLUS_FIVE]);
        console.log('playerState', playerState);
    },
    clearPreBoosters: () => {
        const playerState = Game.store.getState().playerState;
        playerState.inventory[PreBoosterType.CANISTER] = 0;
        playerState.inventory[PreBoosterType.JOKER] = 0;
        playerState.inventory[PreBoosterType.REMOVE_THREE] = 0;
        PlayerActions.setPlayerState(playerState);
        console.log('playerState', playerState);
    },
    setExtraCards: (value: number) => {
        PlayerActions.receiveRewards({
            rewards: [
                {
                    type: RewardType.CARDS,
                    value: value,
                },
            ],
        });
    },
    unlockBackcard: (backcardId?: number) => {
        PlayerActions.unlockBackCard(backcardId);
    },
    setQuestProgress: (questNumber: number, progress: number) => {
        PlayerActions.setPlayerState(
            QuestStateHelper.setQuestProgress(Game.store.getState().playerState, questNumber - 1, progress)
        );
    },
    setQuestProgressByType: (questType: QuestType, progress: number) => {
        PlayerActions.setPlayerState(
            QuestStateHelper.incrementQuestProgressBy(Game.store.getState().playerState, questType, progress)
        );
    },
    setWheelSpins: (spins: number) => {
        const playerState = Game.store.getState().playerState;
        PlayerActions.receiveWheelSpins(spins - playerState.inventory.wheelSpins);
    },
    setPremiumWheelSpins: (spins: number) => {
        const playerState = Game.store.getState().playerState;
        PlayerActions.setPlayerState({
            ...playerState,
            premiumWheelSpinsAmount: spins
        });
        GameActions.loadWorld();
    },
    resetSpunFirstWheels: () => {
        const playerState = Game.store.getState().playerState;
        PlayerActions.setPlayerState({
            ...playerState,
            spunFirstNormalWheel: false,
            spunFirstDiamondWheel: false,
        });
        GameActions.loadWorld();
    },
    cinematicMode: (value: boolean = true) => {
        const state: IGameState = Game.store.getState();

        PlayerActions.setPlayerState({
            ...state.playerState,
            settings: {
                ...state.playerState.settings,
                cinematicMode: value,
            },
        });

        GameActions.loadWorld({
            worldNumber: state.worldState.worldNumber,
            firstComplete: true,
        });
    },
    showState: () => {
        console.log(Game.store.getState());
    },
    setBoosterReward: (boosterType: BoosterType, value: number) => {
        const worldSystem = Game.scene.getActiveScene().getSystem(WorldSystem);
        worldSystem.taskQueue.queue(
            new AnimationTask('RewardBoosters', worldSystem.animator, {
                type: boosterType,
                amount: value,
                node: worldSystem.getCurrentLevelNode(),
            })
        );
        worldSystem.taskQueue.start(worldSystem);
    },
    setBoosterCoins: (type: PlayerTask, value: number) => {
        const worldSystem = Game.scene.getActiveScene().getSystem(WorldSystem);
        worldSystem.taskQueue.queue(
            new AnimationTask(
                'RewardCoins',
                worldSystem.animator,
                {
                    scene: worldSystem.scene,
                    type,
                    value,
                    previousValue: Game.store.getState().playerState.inventory.coins,
                    texture: 'coin_large',
                    increment: 100,
                },
                false
            )
        );
        worldSystem.taskQueue.start(worldSystem);
    },
    setInitialLogin: (days: number) => {
        let state: IGameState = Game.store.getState();
        const newDate: Date = new Date(new Date(DateHelper.now.setDate(DateHelper.now.getDate() - days)));
        state = TimerStateHelper.setTimer(state, TimerType.INITIAL_LOGIN, newDate);
        state = TimerStateHelper.setTimer(state, TimerType.LAST_LOGIN, DateHelper.yesterday);

        PlayerActions.setPlayerState({
            ...state.playerState,
        });

        return console.warn('Initial Login Date changed to: ' + newDate);
    },
    setPlayerPurchases: (purchases: number) => {
        const state: IGameState = Game.store.getState();
        PlayerActions.setPlayerState({
            ...state.playerState,
            purchases,
        });
        GameActions.loadWorld({ worldNumber: state.worldState.worldNumber });
    },
    setDifficultyReduction: (difficultyReduction: number) => {
        const state = PlayerStateHelper.setDifficultyReduction(Game.store.getState(), difficultyReduction);
        PlayerActions.setPlayerState(state.playerState);
    },
    showPopup: (popupName: any, params: any) => {
        UIActions.showPopup(popupName, params);
    },
    showHardLevelShare: () => {
        const level = Game.store.getState().levelState.levelNumber;
        UIActions.showPopup(PopupID.HARD_LEVEL_SHARE_POPUP, {levelNumber: level, firstComplete: true});
    },
    setStarChestStarCount: (count: number) => {
        let playerState = Game.store.getState().playerState;
        const newInitialStarCount = ProgressStateHelper.getStarsCount(playerState, LevelType.Core) - count;
        playerState = StarChestStateHelper.setStarChestInitialStars(playerState, newInitialStarCount);

        PlayerActions.setPlayerState(playerState);

        const tier = StarChestStateHelper.getStarChestUnlockTier(playerState);

        GameCheats.upgradeStarChest(tier);
    },
    setStarChestLevel: (levelNumber: number | null, reload: boolean = true) => {
        const playerState = StarChestStateHelper.setStarChestLevel(Game.store.getState().playerState, levelNumber);

        PlayerActions.setPlayerState(playerState);

        if (reload) {
            GameActions.loadWorld();
        }
    },
    upgradeStarChest: (tier: number) => {
        if (!StarChestHelper.tierExists(tier)) {
            console.error(`Star Chest tier '${tier}' does not exist`);
            return;
        }

        const state = Game.store.getState();
        if (state.playerState.starChestLevel === null) {
            console.error('No star chest currently exists');
            return;
        }

        const worldSystem = Game.scene.getActiveScene().getSystem(WorldSystem);
        if (!worldSystem) {
            console.error('This command only works from World Map');
            return;
        }

        const levelNode = worldSystem.levelNodes.get(state.playerState.starChestLevel);
        const starChest = levelNode.getChildByName('star_chest');
        if (starChest) {
            StartAnimationClip(
                new StarChestUpgrade(starChest, {
                    tier: tier,
                    updateTexture: false,
                })
            );
        } else {
            console.error(`Star chest not found`);
        }
    },
    playStarChestStarsRewardAnimation: (amount: number, levelNumber?: number) => {
        UIActions.hidePopup(PopupID.LEVEL_POPUP);
        const state = Game.store.getState();
        const previousStarCount: number = StarChestStateHelper.getStarChestStarCount(state.playerState);

        const playerState = PlayerStateHelper.addStateTaskDatas(state.playerState, [
            {
                task: PlayerTask.STAR_CHEST_STAR_REWARD,
                amount,
                previousStarCount,
                currentStarChestTier: StarChestStateHelper.getStarChestUnlockTier(state.playerState),
                levelNumber,
            },
        ]);
        PlayerActions.setPlayerState(playerState);
    },
    playStarChestFallAnimation: () => {
        UIActions.hidePopup(PopupID.LEVEL_POPUP);

        const state = Game.store.getState();
        const worldSystem = Game.scene.getActiveScene().getSystem(WorldSystem);
        const levelNode = worldSystem.getLevelNode(state.playerState.starChestLevel);
        const starChest = levelNode.findChildByName('star_chest');
        worldSystem.taskQueue.queue(new AnimationTask('StarChestFall', starChest, {}));
        worldSystem.taskQueue.start(worldSystem);
    },
    playStarChestFTUX: () => {
        UIActions.hidePopup(PopupID.LEVEL_POPUP);
        const state = Game.store.getState();

        const playerState = PlayerStateHelper.addStateTaskDatas(state.playerState, [
            {
                task: PlayerTask.STAR_CHEST_FTUX,
                starChestLevel: state.playerState.starChestLevel,
            },
        ]);
        PlayerActions.setPlayerState(playerState);
    },
    setMissionProgress: (questIndex: number, progress: number) => {
        const state = Game.store.getState();
        const quest = state.playerState.quests[questIndex];
        let playerState;
        if (progress > quest.progress) {
            playerState = QuestStateHelper.incrementQuestProgressBy(
                state.playerState,
                quest.type,
                progress - quest.progress
            );
        } else {
            playerState = QuestStateHelper.setQuestProgress(state.playerState, questIndex, progress);
        }
        PlayerActions.setPlayerState(playerState);
    },
    setMissionType: (questIndex: number, questType: QuestType, tier?: number) => {
        const state = Game.store.getState();
        let playerState = {
            ...state.playerState,
            quests: ArrayHelper.changeElement(state.playerState.quests, questIndex, () => {
                return QuestHelper.getNewQuest({
                    currentQuests: state.playerState.quests,
                    forcedQuestType: questType,
                    forcedTier: tier
                });
            }),
        };
        PlayerActions.setPlayerState(playerState);
    },
    claimMissionReward: (questIndex: number) => {
        PlayerActions.claimQuestReward(questIndex);
    },
    completeMissionTimer: (questIndex?: number) => {
        const state = Game.store.getState();
        let playerState = { ...state.playerState };

        if (questIndex === undefined) {
            for (let i = 0; i < questSlotsCount; i++) {
                resetQuestTime(i);
                refreshQuest(i);
            }
        } else {
            resetQuestTime(questIndex);
            refreshQuest(questIndex);
        }

        return;
        function resetQuestTime(index: number) {
            playerState = {
                ...playerState,
                quests: ArrayHelper.changeElement(playerState.quests, index, quest => {
                    return {
                        ...quest,
                        cooldownStartedAt: new Date(DateHelper.now.getTime() - questTimerDuration),
                    };
                }),
            };
        }
        function refreshQuest(index: number) {
            PlayerActions.setPlayerState(QuestStateHelper.refreshQuestAfterCooldown({...state, playerState}, index).playerState);
        }
    },
    setLoginStreak: (value: number) => {
        const clampedValue = MathHelper.clamp(value, 1, Infinity);
        if (clampedValue !== value) {
            console.warn(`Invalid streak length provided, defaulting to: ${clampedValue}`);
        }
        const playerState = Game.store.getState().playerState;
        const { todayClaimWindowEnd, lastClaimWindowEnd } = DailyLoginHelper.getClaimWindows();

        PlayerActions.setPlayerState({
            ...playerState,
            progress: {
                ...playerState.progress,
                // Filter out any rewards that were claimed today to allow a new reward to be collected
                dailyLoginRewardsClaimed: playerState.progress.dailyLoginRewardsClaimed.filter(
                    reward =>
                        !DailyLoginHelper.wasRewardClaimedWithin(reward, lastClaimWindowEnd, todayClaimWindowEnd)
                ),
            },
        });
        PlayerActions.updateLoginStreak(value);
    },
    clearLastSundayPurchase: () => {
        const playerState = Game.store.getState().playerState;
        PlayerActions.setPlayerState({
            ...playerState,
            lastSundayOfferPurchaseTimestamp: 0,
        });
    },
    setDay: (day: number) => {
        day = MathHelper.clamp(day, 0, 6);
        while (DateHelper.now.getDay() !== day) {
            Game.serverTimeOffset -= DateHelper.Day;
        }
        GameActions.loadWorld();
    },
    scheduleInactiveNotification() {
        scheduleInactivityNotification(Game.store.getState(), true);
    },
    setPetLevel: (value: number, reload: boolean = true) => {
        PlayerActions.setPetLevel(value);
        if (reload) {
            GameActions.loadWorld();
        }
    },
    setActivePet: (value: PetId, reload: boolean = true) => {
        let playerState = Game.store.getState().playerState;
        if (!PetsStateHelper.isPetUnlocked(playerState.petsState, value)) {
            playerState = PetsStateHelper.updatePlayerState(
                playerState,
                PetsStateHelper.unlockPet(playerState.petsState, value)
            );
        }
        playerState = PetsStateHelper.updatePlayerState(
            playerState,
            PetsStateHelper.setActivePet(playerState.petsState, value)
        );

        PlayerActions.setPlayerState(playerState);
        if (reload) {
            GameActions.loadWorld();
        }
    },
    clearPetData: (value: boolean = false, reload: boolean = true) => {
        const state = Game.store.getState();
        PlayerActions.setPlayerState({
            ...FTUXStateHelper.clearFTUXProgress(state, FTUXType.PET_UNLOCK).playerState,
            petsState: { ...initialPetsState, petInDailyLogin: value },
            petLevel: null,
        });
        if (value) {
            cheats.setLoginStreak(petDayIndex);
        }
        if (reload) {
            GameActions.loadWorld();
        }
    },
    encryptPayload: (payload: EntryPoint) => {
        return encryptPayload(payload);
    },
    decryptPayload: (cipherText: string) => {
        return decryptPayload(cipherText);
    },
    movePet: (nodesToMove: number, reload: boolean) => {
        const system = Game.scene.getActiveScene().getSystem(WorldSystem);
        const state = Game.store.getState();
        const petLevel = state.playerState.petLevel;
        const petData = PetsHelper.getPetData(state.playerState.petsState.activePet);

        if (petLevel === null) return;

        const pet = system.pet;

        pet.initialized
            .then(() => {
                const system = Game.scene.getActiveScene().getSystem(WorldSystem);
                const points: Point[] = [];

                for (let i = petLevel + 1; i <= petLevel + nodesToMove; i++) {
                    points.push(getNodePosition(i));
                }

                StartAnimationClip(
                    new MovePet(pet, {
                        points,
                        exitAnimations: [PetAnimations.WALKING_TO_IDLE, PetAnimations.IDLE],
                    })
                );
                return;
                function getNodePosition(levelNumber: number): Point {
                    return Point.add(system?.getLevelNode(levelNumber).getPetPosition(), petData.nodeWalkOffset);
                }
            })
            .catch(error => console.error(error));
    },
    startPetMiniGame: (props: { forceJackpotWin?: boolean } = {}) => {
        const params: MyPetMiniGameSceneParams = { forceJackpotWin: props.forceJackpotWin };
        GameActions.loadMyPetMiniGame(params);
    },
    setServerTime: (time: number) => {
        Game.serverTimeOffset = time;
        GameActions.loadWorld();
    },
    enablePurchases: () => {
        FBInstant.payments.purchaseAsync = (purchaseConfig: { productID: string }) =>
            Promise.resolve({
                developerPayload: '',
                paymentID: '',
                productID: purchaseConfig.productID,
                purchaseTime: '',
                purchaseToken: '',
                signedRequest: '',
            });
        FBInstant.payments.consumePurchaseAsync = () => Promise.resolve(true);
    },
    setMarketingCampaign: (campaign: string) => {
        let playerState = Game.store.getState().playerState;
        playerState = {
            ...playerState,
            attributionCampaignID: campaign || initialPlayerState.attributionCampaignID,
        };
        PlayerActions.setPlayerState(playerState);
    },
    getMarketingCampaign: () => {
        return Game.store.getState().playerState.attributionCampaignID;
    },
    debugXPromo: () => {
        const state = Game.store.getState();
        GameActions.setState({
            ...state,
            perSessionData: {
                ...state.perSessionData,
                debugXPromo: true,
            },
        });
    },
    showFriendHelper: (value: boolean = true) => {
        setForceHelperFriend(value);
    },
    resetLastClaimedSpinLevel: () => {
        const playerState = Game.store.getState().playerState;
        PlayerActions.setPlayerState({
            ...playerState,
            lastClaimedSpinLevel: 0
        });
        GameActions.loadWorld();
    },
    sendCustomUpdate: (data: {
        friendId: string,
        payload?: IMessagePayload,
        text?: string,
        imageBase64?: string,
        strategy?: string,
        cta?: string
    }) => {
        let {
            text = "Custom Update",
            imageBase64 = PayloadHelper.getScreenshotBase64(),
            payload = {},
            friendId,
            strategy,
            cta
        } = data;

        Game.sdk.customUpdate(text, imageBase64, friendId, payload, strategy, cta);
    },
    setTimer: (timerType: TimerType, date: Date | null) => {
        Game.store.dispatch(TimerActions.updateTimer({
            timer: timerType,
            time: date
        }))
    },
    clearFarm: () => {
        PlayerActions.setPlayerState({
            ...Game.store.getState().playerState,
            farmState: initialFarmPlayerState
        })
    },
    clearFarmAreas: () => {
        FarmActions.clearFarmAreas();
        if(Game.store.getState().activeScene.result === GameScene.FARM) {
            GameActions.loadFarmScene({loadingSceneId: farmTransitionSceneId});
        } else {
            GameActions.loadWorld();
        }
    },
    setPlayerState: (playerState: IPlayerState) => {
        PlayerActions.setPlayerState(playerState);
    },
    updatePlayerState: (playerState: Partial<IPlayerState>) => {
        const state = Game.store.getState();
        PlayerActions.setPlayerState({
            ...state.playerState,
            ...playerState
        });
    },
    clearEventQuests: () => {
        PlayerActions.setPlayerState({
            ...Game.store.getState().playerState,
            eventQuests: []
        });
    },
    clearSeenDecorations: () => {
        const state = Game.store.getState();
        PlayerActions.setPlayerState({
            ...state.playerState,
            decoItemsSeen : []
        })
    }
};

type Cheats = typeof cheats;

// Wrap all cheats into another function to detect when they're used
export const GameCheats: Cheats = Object.entries(cheats).reduce(
    (acc, [funcName, func]: [keyof Cheats, Cheats[keyof Cheats]]) => {
        acc[funcName] = function(...args: Array<any>) {
            console.log(`cheat used: ${funcName}`);
            const state = Game.store.getState();
            cheatsUsed = true;
            PlayerActions.setPlayerState({
                ...state.playerState,
                usedCheats: true,
            });
            return func.call(this, ...args);
        };
        return acc;
    },
    {} as Cheats
);
